import { useTranslation } from "react-i18next";
import { Controller, useFormContext } from "react-hook-form";
import { Box, FormHelperText, FormControl, TextField, Typography, FormLabel, Skeleton } from "@mui/material";

import { CreateContractData } from "../../../types/service.type";

const ServiceDetailsFields = ({
  serviceName,
  serviceCode,
  isDataLoading,
}: {
  serviceName?: string;
  serviceCode?: string;
  isDataLoading: boolean;
}) => {
  const { t } = useTranslation("goService", { keyPrefix: "createContractPage" });
  const {
    control,
    formState: { errors },
  } = useFormContext<CreateContractData>();

  return (
    <Box sx={styles.container}>
      <Box sx={styles.serviceNameContainer}>
        <Typography sx={styles.serviceNameLabel}>{t("serviceName")}</Typography>
        {isDataLoading ? (
          <Skeleton variant="text" width="80px" height="28px" />
        ) : (
          <Typography sx={styles.serviceNameValue}>{serviceName || "--"}</Typography>
        )}
      </Box>
      <Typography sx={styles.serviceCode}>
        {t("serviceCode")}
        {isDataLoading ? <Skeleton variant="text" width="50px" height="20px" /> : serviceCode || "--"}
      </Typography>
      <FormControl fullWidth component="fieldset" sx={styles.formControl} error={!!errors.description}>
        <FormLabel sx={styles.formLabel}>{t("description")}</FormLabel>
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              disabled={isDataLoading}
              fullWidth
              multiline
              rows={5}
              sx={styles.textField}
              error={!!errors.description}
            />
          )}
        />
        {errors.description && <FormHelperText error>{t(errors.description.message)}</FormHelperText>}
      </FormControl>
    </Box>
  );
};

export default ServiceDetailsFields;

const styles = {
  container: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  serviceNameContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  serviceNameLabel: {
    fontFamily: "Montserrat",
    color: "var(--Dark---600, #707070)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
  },
  serviceNameValue: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  serviceCode: {
    fontFamily: "Montserrat",
    color: "var(--Dark---600, #707070)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  formControl: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  formLabel: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  textField: {
    "& .MuiInputBase-root.MuiOutlinedInput-root": {
      borderRadius: "8px",
      border: "none",
      background: "var(--Gray---50, #F5F6F5)",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
};
